/**
 * Vendor image selection — single source of truth.
 *
 * Two render roles are supported by this module:
 *
 *   HERO IMAGE (big media on profile + browse cards)
 *     getHeroImage() — cover_image → first portfolio_photo → profile_photo
 *                       → owner_photo → null
 *
 *   AVATAR (round business avatar on cards, selection rows, edit form)
 *     getVendorAvatar() — profile_photo → owner_photo → null
 *
 * The `owner_photo` field is a read-only, display-time fallback supplied by
 * the backend (services/profile_image.attach_owner_photo). It sources the
 * same user's customer_profile.profile_photo so a vendor without a business
 * logo still shows *something* personal, without any data duplication or
 * sync job. Once a vendor uploads their own profile_photo it wins the chain
 * automatically; when they remove it, the owner fallback resumes.
 *
 * Customer-facing personal surfaces (CustomerDashboard avatar, ProfileForm)
 * must NOT use these helpers — they read the customer_profile photo
 * directly so personal identity stays cleanly separated from business
 * identity.
 */
export function getHeroImage(vendor) {
  if (!vendor) return null;
  const cover = typeof vendor.cover_image === 'string' ? vendor.cover_image.trim() : '';
  if (cover) return cover;
  const portfolioFirst = (vendor.portfolio_photos || []).find(
    (p) => typeof p === 'string' && p.trim() !== ''
  );
  if (portfolioFirst) return portfolioFirst;
  return vendor.profile_photo || vendor.owner_photo || null;
}

/**
 * Vendor avatar selection — single source of truth for the round avatar
 * used in the vendor detail header, Plan Event rows, vendor-selection
 * preview, the vendor dashboard self-view, and the vendor edit form
 * preview.
 *
 * Chain (strict):
 *   1. vendor.profile_photo     (business identity — set by the vendor)
 *   2. vendor.owner_photo       (personal/customer photo — read-only
 *                                fallback attached by the backend when
 *                                profile_photo is empty. See
 *                                backend/services/profile_image.py)
 *   3. null                     (caller renders its own placeholder)
 *
 * Customer/user personal surfaces (e.g. CustomerDashboard) must NOT use
 * this helper — they read the customer_profile photo directly to preserve
 * the separation between personal identity and business identity.
 */
export function getVendorAvatar(vendor) {
  if (!vendor) return null;
  const isUsable = (v) => {
    if (typeof v !== 'string') return false;
    const s = v.trim();
    if (!s) return false;
    // Reject seed/placeholder URLs that resolve to nothing in-browser.
    // Mirrors the filter used in PlanEvent gallery for consistency.
    if (/example\.com/i.test(s)) return false;
    if (/placeholder/i.test(s)) return false;
    return true;
  };
  if (isUsable(vendor.profile_photo)) return vendor.profile_photo;
  if (isUsable(vendor.owner_photo)) return vendor.owner_photo;
  return null;
}

/**
 * True when the vendor is currently displaying the fallback (owner) photo
 * rather than its own business photo. Used by the vendor edit UI to show
 * the "Using your personal photo" hint (Case A).
 */
export function isUsingOwnerPhotoFallback(vendor) {
  if (!vendor) return false;
  const own = typeof vendor.profile_photo === 'string' ? vendor.profile_photo.trim() : '';
  if (own) return false;
  const owner = typeof vendor.owner_photo === 'string' ? vendor.owner_photo.trim() : '';
  return !!owner;
}

/**
 * True when the user has a personal/customer photo available, regardless of
 * whether the vendor photo is also set. Powers the Case B source note
 * ("Using your business photo · Personal photo available") on vendor
 * edit/self-view surfaces. Never alters display precedence.
 */
export function hasOwnerPhoto(vendor) {
  if (!vendor) return false;
  const owner = typeof vendor.owner_photo === 'string' ? vendor.owner_photo.trim() : '';
  return !!owner;
}
